import React, { useCallback, useEffect } from 'react';

import { MultiSelectDropdown } from '@components/index';
import useNotify from '@hooks/notify';
import { DropdownOption } from 'types/dropdown';

import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '../constants/common';

type Mapper<T> = (data: T) => { id: string; value: string; label: string }[];

type AsyncMultiSelectDropdownProps<T> = {
  label: string;
  placeholder: string;
  fetchOptions?: () => Promise<T>;
  map?: Mapper<T>;
  selectedOptions: DropdownOption[];
  setSelectedOptions: (
    value: DropdownOption[],
    options: DropdownOption[]
  ) => void;
  className?: string;
  chipToolTipId?: string;
  errorAlertMessage?: string;
  listItems?: { id: string; value: string; label: string }[];
  mustSelectAtLeastOne?: boolean;
};

const AsyncMultiSelectDropdown = <T,>({
  label,
  placeholder,
  fetchOptions,
  map,
  selectedOptions,
  setSelectedOptions,
  className,
  chipToolTipId,
  errorAlertMessage = 'Error fetching options',
  listItems = [],
  mustSelectAtLeastOne = false
}: AsyncMultiSelectDropdownProps<T>) => {
  const { createErrorAlert } = useNotify();
  const [options, setOptions] = React.useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const hasSelectedListDefaults = React.useRef(false);

  const initialise = useCallback(async () => {
    if (!fetchOptions || !map) return;
    try {
      setIsLoading(true);
      const res = await fetchOptions();
      const mapped = map(res);
      setOptions(mapped);
      setSelectedOptions(mapped, mapped); // both selected items and list is the same
    } catch {
      setOptions([]);
      createErrorAlert(errorAlertMessage);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectOption = (selectedItems: DropdownOption[]) => {
    if (mustSelectAtLeastOne && selectedItems.length === 0) {
      createErrorAlert('Please select at least one option');
    } else {
      setSelectedOptions(selectedItems, options);
    }
  };

  useEffect(() => {
    initialise();
  }, [initialise]);

  // listItems can resolve asynchronously (e.g. fetched by a parent hook), so
  // sync options whenever they arrive and select all by default just once.
  useEffect(() => {
    if (fetchOptions && map) return;
    setOptions(listItems);
    if (listItems.length > 0 && !hasSelectedListDefaults.current) {
      setSelectedOptions(listItems, listItems); // both selected items and list is the same
      hasSelectedListDefaults.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems]);

  return (
    <>
      <label className="mb-2 block text-sm font-semibold text-cofeeNeutral10">
        {label}
      </label>
      <MultiSelectDropdown
        label={label}
        searchBarPlaceholder={placeholder}
        options={options}
        onChange={handleSelectOption}
        selectedOptions={selectedOptions}
        className={className}
        chipToolTipId={chipToolTipId}
        isLoading={isLoading}
        dropDownHeaderStyle={`${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
        chipClassName="!bg-grey10"
        plusNChipBgColor="bg-grey10"
      />
    </>
  );
};

export default AsyncMultiSelectDropdown;
